// CVE projectile system. CVEs fly in from a random edge of the arena, bounce
// off walls + environment items via Phaser arcade collisions, and despawn
// after a lifetime. Hitting the player drains 1 health segment; hitting an
// environment item with a hidden asset triggers a 5-second exploit timer
// (handled in AssetSystem.handleCveHit).
//
// Spawning runs through a hand-tuned wave schedule: each entry sets the
// simultaneous CVE count to maintain for `durationMs`, followed by
// `gapAfterMs` of total silence before the next wave. The last wave has
// `durationMs: -1` (infinite — runs until the boss arrives or time expires).
//
// Replenishment cooldown: when a CVE dies during normal play (player hit,
// lifetime expiry, exploit-trigger destroyAll, etc.) a fresh cooldown is set
// — the wave only respawns the missing CVE once `spawnIntervalMs` has
// elapsed. Without this, the update() loop would respawn instantly on the
// next tick after a death, defeating the spacing.

import Phaser from 'phaser';
import { brandHex } from '../brand';
import { config } from '../config';
import { SPRITE_KEYS } from './sprites';

interface CveWave {
  count: number;
  durationMs: number; // -1 = run until the system stops
  gapAfterMs: number;
}

const WAVES: CveWave[] = [
  { count: 1, durationMs: 10_000, gapAfterMs: 10_000 },
  { count: 2, durationMs: 10_000, gapAfterMs: 30_000 },
  { count: 4, durationMs: 10_000, gapAfterMs: 10_000 },
  { count: 1, durationMs: -1,     gapAfterMs: 0 },
];

export class CveSystem {
  private scene: Phaser.Scene;
  private group: Phaser.Physics.Arcade.Group;
  private bounds: Phaser.Geom.Rectangle;
  private active = false;
  /** True while a hotspot exploit is in progress — CVE spawning halts so
   *  the player has a clear shot at the in-place defence. */
  private paused = false;

  // wave state
  private waveIndex = 0;
  private waveActive = false;
  private targetCount = 0;
  private waveTimer?: Phaser.Time.TimerEvent;
  private gapTimer?: Phaser.Time.TimerEvent;
  /** Earliest time a respawn is allowed (ms). Set whenever a CVE departs
   *  during normal play; the replenish loop in update() waits for it. */
  private nextSpawnAt = 0;

  constructor(scene: Phaser.Scene, bounds: Phaser.Geom.Rectangle) {
    this.scene = scene;
    this.bounds = bounds;
    this.group = scene.physics.add.group({ collideWorldBounds: false });
  }

  start() {
    this.active = true;
    // first wave kicks in after firstSpawnDelayMs so the player gets a beat
    this.gapTimer = this.scene.time.delayedCall(
      config.cves.firstSpawnDelayMs,
      () => this.startWave(0),
    );
  }

  stop() {
    this.active = false;
    this.waveActive = false;
    this.targetCount = 0;
    this.waveTimer?.remove(false);
    this.gapTimer?.remove(false);
    this.waveTimer = undefined;
    this.gapTimer = undefined;
  }

  destroyAll() {
    this.group.children.each((c) => {
      const cve = c as Phaser.Physics.Arcade.Sprite;
      const emitter = cve.getData('emitter') as Phaser.GameObjects.Particles.ParticleEmitter | undefined;
      emitter?.destroy();
      cve.destroy();
      return null;
    });
  }

  getGroup(): Phaser.Physics.Arcade.Group {
    return this.group;
  }

  /**
   * Halt all spawning + replenishment until resumeSpawning(). Wave / gap
   * timers are also paused so the wave clock doesn't drain while the
   * player is dealing with the in-place exploit. All in-flight CVEs are
   * destroyed — the one that triggered the exploit and its siblings — so
   * the arena is clean for the defensive window. resumeSpawning() will
   * then repopulate the wave's target count from update().
   */
  pauseSpawning() {
    if (this.paused) return;
    this.paused = true;
    if (this.waveTimer) this.waveTimer.paused = true;
    if (this.gapTimer) this.gapTimer.paused = true;
    this.destroyAll();
  }

  resumeSpawning() {
    if (!this.paused) return;
    this.paused = false;
    if (this.waveTimer) this.waveTimer.paused = false;
    if (this.gapTimer) this.gapTimer.paused = false;
    // Fresh cooldown so the player gets a beat after the in-place defence
    // window closes — replenishment doesn't snap in on the very next tick.
    const [lo, hi] = config.cves.spawnIntervalMs;
    this.nextSpawnAt = this.scene.time.now + Phaser.Math.Between(lo, hi);
  }

  /** Begin a wave: lock in target count, prime the arena with the initial
   *  CVEs, and schedule the wave-end + gap. */
  private startWave(index: number) {
    if (!this.active) return;
    const safeIndex = Math.min(index, WAVES.length - 1);
    const wave = WAVES[safeIndex];
    this.waveIndex = safeIndex;
    this.waveActive = true;
    this.targetCount = wave.count;
    // Wave kickoff: priming spawns are immediate; the cooldown is for
    // mid-wave respawns only.
    this.nextSpawnAt = 0;

    // Prime the arena. update() will replenish if the player kills any
    // before the wave ends.
    for (let i = 0; i < wave.count; i++) this.spawn();

    if (wave.durationMs > 0) {
      this.waveTimer = this.scene.time.delayedCall(
        wave.durationMs,
        () => this.endWave(wave.gapAfterMs),
      );
    }
  }

  /** Wave is over: clear the arena, hold for gapMs, then advance. */
  private endWave(gapMs: number) {
    this.waveActive = false;
    this.targetCount = 0;
    this.waveTimer = undefined;
    this.destroyAll();
    if (!this.active) return;

    if (gapMs > 0) {
      this.gapTimer = this.scene.time.delayedCall(gapMs, () => {
        this.gapTimer = undefined;
        this.startWave(this.waveIndex + 1);
      });
    } else {
      this.startWave(this.waveIndex + 1);
    }
  }

  spawn() {
    // Pick a random edge to fly in from. Position is just inside the wall on
    // that edge; velocity points across the arena with a randomised angle so
    // CVEs don't all travel along axis-aligned tracks.
    const edge = Phaser.Math.Between(0, 3); // 0=top, 1=right, 2=bottom, 3=left
    const inset = 24;
    const spd = config.cves.speed;
    let x = 0, y = 0, vx = 0, vy = 0;

    switch (edge) {
      case 0: { // top
        x = Phaser.Math.Between(this.bounds.x + inset, this.bounds.right - inset);
        y = this.bounds.y + inset;
        const a = Phaser.Math.FloatBetween(Math.PI * 0.20, Math.PI * 0.80); // pointing down + spread
        vx = Math.cos(a) * spd;
        vy = Math.sin(a) * spd;
        break;
      }
      case 1: { // right
        x = this.bounds.right - inset;
        y = Phaser.Math.Between(this.bounds.y + inset, this.bounds.bottom - inset);
        const a = Phaser.Math.FloatBetween(Math.PI * 0.70, Math.PI * 1.30); // pointing left
        vx = Math.cos(a) * spd;
        vy = Math.sin(a) * spd;
        break;
      }
      case 2: { // bottom
        x = Phaser.Math.Between(this.bounds.x + inset, this.bounds.right - inset);
        y = this.bounds.bottom - inset;
        const a = Phaser.Math.FloatBetween(Math.PI * 1.20, Math.PI * 1.80); // pointing up
        vx = Math.cos(a) * spd;
        vy = Math.sin(a) * spd;
        break;
      }
      default: { // left
        x = this.bounds.x + inset;
        y = Phaser.Math.Between(this.bounds.y + inset, this.bounds.bottom - inset);
        const a = Phaser.Math.FloatBetween(-Math.PI * 0.30, Math.PI * 0.30); // pointing right
        vx = Math.cos(a) * spd;
        vy = Math.sin(a) * spd;
        break;
      }
    }

    const cve = this.group.create(x, y, SPRITE_KEYS.cve) as Phaser.Physics.Arcade.Sprite;
    cve.setDisplaySize(28, 28);
    cve.setDepth(8);
    const body = cve.body as Phaser.Physics.Arcade.Body;
    body.setCircle(11, 1, 1);
    body.setBounce(1, 1);
    body.setVelocity(vx, vy);
    cve.setData('birthMs', this.scene.time.now);

    // Whenever this CVE departs (lifetime expiry, player collision, hidden
    // asset hit, etc.) reset the respawn cooldown so update() doesn't
    // refill the wave on the very next frame.
    cve.once(Phaser.GameObjects.Events.DESTROY, () => {
      if (this.active && this.waveActive && !this.paused) {
        const [lo, hi] = config.cves.spawnIntervalMs;
        this.nextSpawnAt = this.scene.time.now + Phaser.Math.Between(lo, hi);
      }
    });

    // trailing red glow that follows the body
    const emitter = this.scene.add.particles(0, 0, SPRITE_KEYS.pixel, {
      follow: cve,
      lifespan: 220,
      speed: 0,
      quantity: 1,
      frequency: 30,
      tint: brandHex.threat,
      alpha: { start: 0.6, end: 0 },
      scale: { start: 6, end: 0 },
      blendMode: 'ADD',
    });
    cve.setData('emitter', emitter);

    // fade-in (alpha only — scale tweens were resetting the body and
    // freezing the CVE in place at spawn).
    cve.setAlpha(0);
    this.scene.tweens.add({
      targets: cve,
      alpha: 1,
      duration: 180,
      ease: 'Cubic.Out',
    });
  }

  /** Call from scene.update(). Expires CVEs past their lifetime and refills
   *  any that died mid-wave so we maintain the wave's target count. */
  update() {
    const now = this.scene.time.now;
    this.group.children.each((c) => {
      const cve = c as Phaser.Physics.Arcade.Sprite;
      const birth = cve.getData('birthMs') as number;
      if (now - birth > config.cves.lifetimeMs) {
        const emitter = cve.getData('emitter') as Phaser.GameObjects.Particles.ParticleEmitter | undefined;
        emitter?.destroy();
        cve.destroy();
      }
      return null;
    });

    // Replenish the wave's count if anything died mid-wave (player hit or
    // lifetime expiry). Spawns one CVE at a time, gated by the cooldown
    // set on the previous death — never instantaneous. Outside a wave or
    // while paused, never spawns.
    if (this.active && this.waveActive && !this.paused) {
      const alive = this.group.countActive(true);
      if (alive < this.targetCount && now >= this.nextSpawnAt) {
        this.spawn();
      }
    }
  }
}
